import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';

import {
  SurveyFieldDto,
  SurveyFieldDtoTypeEnum,
} from 'apps/marsinet/src/app/typescript-axios';
import { InputTextarea } from 'primereact/inputtextarea';

interface ModalQuestionsProps {
  visible: boolean;
  onHide: () => void;
  template: SurveyFieldDto[];
  onTemplateChange: (template: SurveyFieldDto[]) => void;
}

const ModalQuestions: React.FC<ModalQuestionsProps> = ({
  visible,
  onHide,
  template,
  onTemplateChange,
}) => {
  const { t } = useTranslation();
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState<
    (typeof SurveyFieldDtoTypeEnum)[keyof typeof SurveyFieldDtoTypeEnum] | ''
  >('');
  const [fieldRequired, setFieldRequired] = useState(true);
  const [fieldAllowedValues, setFieldAllowedValues] = useState<string[]>([]);
  const [allowedValueInput, setAllowedValueInput] = useState('');
  const [dependsOnFieldName, setDependsOnFieldName] = useState<string>('');
  const [dependsOnFieldValue, setDependsOnFieldValue] = useState<string>('');
  const [fieldDescription, setFieldDescription] = useState('');
  const [fieldNameError, setFieldNameError] = useState<string>('');
  const toastRef = useRef<Toast>(null);

  const handleAddField = () => {
    const fieldNameExists = template.some((field) => field.name === fieldName);

    if (fieldNameExists) {
      return;
    }

    if (fieldName && fieldType) {
      const newField: SurveyFieldDto = {
        name: fieldName,
        type: fieldType as (typeof SurveyFieldDtoTypeEnum)[keyof typeof SurveyFieldDtoTypeEnum],
        required: fieldRequired,
        ...(fieldDescription.trim() && {
          description: fieldDescription.trim(),
        }),
        ...(fieldAllowedValues.length > 0 && {
          allowed_values: fieldAllowedValues,
        }),
        ...(dependsOnFieldName &&
          dependsOnFieldValue && {
          condition: {
            field_name: dependsOnFieldName,
            value: dependsOnFieldValue,
          },
        }),
      };
      onTemplateChange([...template, newField]);
      setFieldName('');
      setFieldType('');
      setFieldRequired(true);
      setFieldDescription('');
      setFieldAllowedValues([]);
      setAllowedValueInput('');
      setDependsOnFieldName('');
      setDependsOnFieldValue('');
      setFieldNameError('');
      onHide();
    }
  };

  const handleAddAllowedValue = () => {
    if (allowedValueInput.trim()) {
      setFieldAllowedValues([...fieldAllowedValues, allowedValueInput.trim()]);
      setAllowedValueInput('');
    }
  };

  const handleRemoveAllowedValue = (index: number) => {
    const updatedValues = fieldAllowedValues.filter((_, i) => i !== index);
    setFieldAllowedValues(updatedValues);
  };

  const getAvailableFieldsForDependency = () => {
    return template.filter(
      (field) =>
        field.type === SurveyFieldDtoTypeEnum.Select ||
        field.type === SurveyFieldDtoTypeEnum.MultiSelect
    );
  };

  const getSelectedFieldAllowedValues = () => {
    if (!dependsOnFieldName) return [];
    const selectedField = template.find(
      (field) => field.name === dependsOnFieldName
    );
    return selectedField?.allowed_values || [];
  };

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    if (value.trim() === '') {
      setFieldNameError('');
    } else {
      const isDuplicate = template.some((field) => field.name === value);
      setFieldNameError(
        isDuplicate ? t('pages.surveys.question_name_duplicate') : ''
      );
    }
  };

  const isSelectType =
    fieldType === SurveyFieldDtoTypeEnum.Select ||
    fieldType === SurveyFieldDtoTypeEnum.MultiSelect;

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header={t('pages.surveys.survey_questions')}
        visible={visible}
        onHide={onHide}
        className="mx-2 md:w-12 lg:w-8"
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <div className="grid formgrid p-fluid">
          <div className="col-12">
            <div className="grid formgrid p-fluid border-round-lg surface-ground p-3">
              {/* Row 1: Title + Required */}
              <div className="field col-12 md:col-8">
                <div className="flex justify-content-between mb-2">
                  <label className="font-bold">
                    {t('pages.surveys.question_title')} <span className="p-error">*</span>
                  </label>
                  <small className="text-color-secondary">
                    {fieldName.length}/50
                  </small>
                </div>
                <InputText
                  value={fieldName}
                  onChange={(e) => handleFieldNameChange(e.target.value)}
                  placeholder={t('pages.surveys.question_title')}
                  className={fieldNameError ? 'ng-invalid ng-touched' : ''}
                  maxLength={50}
                />
                {fieldNameError && (
                  <small className="p-error block mt-1">{fieldNameError}</small>
                )}
              </div>

              <div className="field col-12 md:col-4">
                {/* invisible label keeps the input aligned with the title field above the switch row */}
                <div className="flex align-items-center justify-content-center gap-2 h-full">
                  <label className="font-bold">
                    {t('pages.surveys.required')}
                  </label>
                  <InputSwitch
                    checked={fieldRequired}
                    onChange={(e) => setFieldRequired(e.value)}
                  />
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="field col-12">
                <div className="flex justify-content-between mb-2">
                  <label className="font-bold">
                    {t('pages.surveys.question_description')}
                  </label>
                  <small className="text-color-secondary">
                    {fieldDescription.length}/100
                  </small>
                </div>
                <InputTextarea
                  value={fieldDescription}
                  onChange={(e) => setFieldDescription(e.target.value)}
                  placeholder={t('pages.surveys.question_description')}
                  maxLength={100}
                  className="block m-0"
                />
              </div>

              {/* Row 3: Type + Options */}
              <div className="field col-12 md:col-4">
                <label className="font-bold block mb-2">
                  {t('pages.surveys.question_type')} <span className="p-error">*</span>
                </label>
                <Dropdown
                  value={fieldType}
                  onChange={(e) => {
                    setFieldType(e.target.value);
                    if (
                      e.target.value !== SurveyFieldDtoTypeEnum.Select &&
                      e.target.value !== SurveyFieldDtoTypeEnum.MultiSelect
                    ) {
                      setFieldAllowedValues([]);
                      setAllowedValueInput('');
                    }
                  }}
                  options={[
                    {
                      label: t('pages.surveys.field_types.text'),
                      value: SurveyFieldDtoTypeEnum.Text,
                    },
                    {
                      label: t('pages.surveys.field_types.number'),
                      value: SurveyFieldDtoTypeEnum.Number,
                    },
                    {
                      label: t('pages.surveys.field_types.select'),
                      value: SurveyFieldDtoTypeEnum.Select,
                    },
                    {
                      label: t('pages.surveys.field_types.multiselect'),
                      value: SurveyFieldDtoTypeEnum.MultiSelect,
                    },
                  ]}
                  placeholder={t('pages.surveys.select')}
                />
              </div>

              {isSelectType && (
                <>
                  <div className="field col-12 md:col-5">
                    <label className="font-bold block mb-2">
                      {t('pages.surveys.question_options')} <span className="p-error">*</span>
                    </label>
                    <InputText
                      value={allowedValueInput}
                      onChange={(e) => setAllowedValueInput(e.target.value)}
                      placeholder={t('pages.surveys.add_question_option')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddAllowedValue();
                        }
                      }}
                    />
                  </div>

                  <div className="field col-12 md:col-3">
                    <label className="font-bold block mb-2">&nbsp;</label>
                    <Button
                      type="button"
                      icon="pi pi-plus"
                      label={t('pages.surveys.add_question_option')}
                      onClick={handleAddAllowedValue}
                      className="w-full"
                      disabled={!allowedValueInput.trim()}
                    />
                  </div>

                  {fieldAllowedValues.length > 0 && (
                    <div className="field col-12">
                      <p className="font-bold mb-2 mt-0">
                        {t('pages.surveys.added_question_options')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {fieldAllowedValues.map((value, index) => (
                          <div
                            key={index}
                            className="flex align-items-center gap-2 px-3 py-2 border-1 border-round surface-border"
                          >
                            <span>{value}</span>
                            <Button
                              type="button"
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-sm"
                              onClick={() => handleRemoveAllowedValue(index)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Row 4: Dependency fields */}
              {getAvailableFieldsForDependency().length > 0 && (
                <div className="col-12 grid formgrid p-fluid m-0 p-0">
                  <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">
                      {t('pages.surveys.depends_on_field')}
                    </label>
                    <Dropdown
                      value={dependsOnFieldName}
                      onChange={(e) => {
                        setDependsOnFieldName(e.target.value);
                        setDependsOnFieldValue('');
                      }}
                      options={getAvailableFieldsForDependency().map((field) => ({
                        label: field.name,
                        value: field.name,
                      }))}
                      placeholder={t('pages.surveys.select_field')}
                      showClear
                    />
                  </div>

                  {dependsOnFieldName && (
                    <div className="field col-12 md:col-6">
                      <label className="font-bold block mb-2">
                        {t('pages.surveys.depends_on_value')}
                      </label>
                      <Dropdown
                        value={dependsOnFieldValue}
                        onChange={(e) => setDependsOnFieldValue(e.target.value)}
                        options={getSelectedFieldAllowedValues().map((value) => ({
                          label: value,
                          value: value,
                        }))}
                        placeholder={t('pages.surveys.select_value')}
                        showClear
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Row 6: Submit */}
              <div className="col-12 flex justify-content-end">
                <div className="w-4">
                  <Button
                    type="button"
                    label={t('pages.surveys.add_question')}
                    onClick={handleAddField}
                    className="w-full"
                    disabled={
                      !fieldName ||
                      !fieldType ||
                      template.some((field) => field.name === fieldName) ||
                      (isSelectType && fieldAllowedValues.length === 0)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ModalQuestions;
